import { Form, Formik, useFormikContext } from "formik";
import { FC, useCallback, useEffect, useRef, useState } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import PhoneNumber from "components/PhoneNumber";
import TextInput from "components/TextInput";

import { BaseOption } from "entities/common";
import { CompanyItem } from "entities/companies";

import useValidation from "hooks/useValidation";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getCompanyRequest } from "store/slices/companiesSlice";
import { showToast } from "store/slices/toastSlice";

import { EMIRATE_OPTIONS } from "utils/data/emirates";
import { DEFAULT_PHONE_CODE } from "utils/data/phoneCodes";

import { editValidationSchema, validationSchema } from "../validationSchema";

import { isValidNumber } from "libphonenumber-js";
import styles from "../styles.module.scss";

export type CompanyFormValues = {
  companyName: string;
  emirate: string;
  fullName: string;
  email: string;
  position: string;
  phoneNumber: string;
};

type PropTypes = {
  onClose?: () => void;
  onSubmit?: (values: CompanyFormValues) => void;
  mode?: "create" | "edit";
  description?: string;
  initialData?: CompanyFormValues;
  id?: number;
};

type FormFieldsProps = {
  id?: number;
  onCancel: () => void;
  mode: "create" | "edit";
  apiErrors: { detail: Record<string, string> } | null;
  isLoading: boolean;
  company: CompanyItem | null;
  phoneError: boolean;
};

const FormFields: FC<FormFieldsProps> = ({ onCancel, mode, apiErrors, isLoading, id, company, phoneError }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, setErrors } =
    useFormikContext<CompanyFormValues>();
  const isCreate = mode === "create";

  useValidation(formRef, errors);

  const handleFieldBlur = useCallback(
    (field: keyof CompanyFormValues) => () => {
      handleBlur(field);
      setFieldValue(field, values[field].trim());
    },
    [handleBlur, setFieldValue, values],
  );

  const handleEmirateSearch = useCallback(
    async (search: string): Promise<{ options: BaseOption[]; hasMore: boolean }> => {
      const query = search.trim().toLowerCase();
      const options = query
        ? EMIRATE_OPTIONS.filter((option) => option.label.toLowerCase().includes(query))
        : EMIRATE_OPTIONS;
      return { options, hasMore: false };
    },
    [],
  );

  const handleEmirateChange = useCallback(
    (val: string) => {
      setFieldValue("emirate", val);
    },
    [setFieldValue],
  );

  useEffect(() => {
    if (apiErrors) {
      setErrors({
        ...(apiErrors?.detail?.company_name && { companyName: apiErrors.detail.company_name }),
        ...(apiErrors?.detail?.city && { emirate: apiErrors.detail.city }),
        ...(apiErrors?.detail?.full_name && { fullName: apiErrors.detail.full_name }),
        ...(apiErrors?.detail?.email && { email: apiErrors.detail.email }),
        ...(apiErrors?.detail?.position && { position: apiErrors.detail.position }),
        ...(apiErrors?.detail?.phone_number && { phoneNumber: apiErrors.detail.phone_number }),
      });

      dispatch(
        showToast({
          id: "company-validation-errors",
          message: isCreate
            ? "Failed to create company. Please try again later."
            : "Failed to update company. Please try again later.",
          type: "error",
          duration: 3000,
        }),
      );
    }
  }, [apiErrors, dispatch, isCreate]);

  useEffect(() => {
    if (mode === "edit" && id) {
      dispatch(getCompanyRequest(id));
    }
  }, [mode, dispatch, id]);

  useEffect(() => {
    if (company && id) {
      setFieldValue("companyName", company.name || "");
      setFieldValue("emirate", company.city?.label || "");
    }
  }, [company, id]);

  return (
    <div ref={formRef} className={styles.content}>
      <Form onSubmit={handleSubmit}>
        <div className={styles.content}>
          <fieldset className={styles.fieldset}>
            <p className={styles.fieldsetTitle}>Company information</p>
            <div className={styles.fieldsetFields}>
              <TextInput
                id="companyName"
                name="companyName"
                label="Company name"
                placeholder="Company name"
                value={values.companyName}
                onChange={handleChange}
                onBlur={handleFieldBlur("companyName")}
                error={touched.companyName ? errors.companyName : undefined}
                isRequired
                maxLength={100}
              />

              <p className={styles.locationTitle}>Location</p>
              <AsyncSearchableSelect
                label="Emirate"
                placeholder="Select emirate"
                value={values.emirate}
                selectedLabel={values.emirate}
                onSearch={handleEmirateSearch}
                onChange={handleEmirateChange}
                error={touched.emirate ? errors.emirate : undefined}
                isRequired
              />
            </div>
          </fieldset>

          {isCreate && (
            <fieldset className={styles.fieldset}>
              <p className={styles.fieldsetTitle}>Invite first user</p>
              <p className={styles.fieldsetSubtitle}>Create company owner account</p>

              <p className={styles.sectionLabel}>User information</p>
              <div className={styles.fieldsetFields}>
                <div className={styles.row}>
                  <TextInput
                    id="fullName"
                    name="fullName"
                    label="Full name"
                    placeholder="Full name"
                    value={values.fullName}
                    onChange={handleChange}
                    onBlur={handleFieldBlur("fullName")}
                    error={touched.fullName ? errors.fullName : undefined}
                    isRequired
                    maxLength={100}
                  />

                  <TextInput
                    id="email"
                    name="email"
                    label="Email"
                    placeholder="your.email@company.com"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleFieldBlur("email")}
                    error={touched.email ? errors.email : undefined}
                    isRequired
                  />
                </div>

                <div className={styles.row}>
                  <TextInput
                    id="position"
                    name="position"
                    label="Position"
                    placeholder="Position"
                    value={values.position}
                    onChange={handleChange}
                    onBlur={handleFieldBlur("position")}
                    maxLength={100}
                  />

                  <PhoneNumber
                    isRequired
                    value={values.phoneNumber}
                    onChange={(phone) => setFieldValue("phoneNumber", phone)}
                    error={phoneError ? "Invalid phone number" : touched.phoneNumber ? errors.phoneNumber : undefined}
                  />
                </div>
              </div>
            </fieldset>
          )}

          <div className={styles.buttons}>
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>

            <Button type="submit" variant="primary" disabled={isCreate && isLoading}>
              {isCreate ? "Create" : "Update"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

const defaultInitialValues: CompanyFormValues = {
  companyName: "",
  emirate: "",
  fullName: "",
  email: "",
  position: "",
  phoneNumber: DEFAULT_PHONE_CODE,
};

const ModalCreateCompanyContent: FC<PropTypes> = ({
  onClose,
  onSubmit,
  mode = "create",
  description,
  initialData,
  id,
}) => {
  const initialValues = initialData || defaultInitialValues;
  const { successfullyAddedId, successfullyUpdatedId, errors, isLoading, isCompanyLoading, company } = useAppSelector(
    (state: RootStore) => state.companies,
  );
  const [phoneError, setPhoneError] = useState(false);

  const handleCancel = () => {
    onClose?.();
  };

  useEffect(() => {
    if (successfullyAddedId || successfullyUpdatedId) {
      onClose?.();
    }
  }, [successfullyAddedId, successfullyUpdatedId, onClose]);

  return (
    <>
      {!!description && <p className={styles.modalDescription}>{description}</p>}
      <Formik
        initialValues={initialValues}
        validationSchema={mode === "create" ? validationSchema : editValidationSchema}
        onSubmit={(values) => {
          if (mode === "create" && !isValidNumber(values.phoneNumber)) {
            setPhoneError(true);

            return;
          }

          const trimmed = {
            ...values,
            companyName: values?.companyName?.trim(),
            fullName: values?.fullName?.trim(),
            position: values?.position?.trim(),
          };

          onSubmit?.(trimmed);
        }}
        enableReinitialize
      >
        <FormFields
          id={id}
          mode={mode}
          company={company}
          apiErrors={errors}
          phoneError={phoneError}
          onCancel={handleCancel}
          isLoading={isLoading || isCompanyLoading}
        />
      </Formik>
    </>
  );
};

export default ModalCreateCompanyContent;
